import React, { useImperativeHandle, useState } from 'react'
import { ethers } from 'ethers'
import { MiningPoolAbi } from '../../../../utils/config'
import { eventBus } from '../../../../utils/tool'
import { useTranslation } from 'react-i18next'
import { buildEmergencyWithdrawPlan, buildTokenWithdrawPlan } from '../../transactionPlan'
import { getSelectedWalletEthersProvider } from '../../../../utils/selectedWalletProvider'
import ConsoleDialog from '../../../StakingConsole/ConsoleDialog'
import { mapStakingError } from '../../../StakingConsole/errorMessages'
import { TransactionSuccessContent } from '../../../StakingConsole/TransactionSuccess'
import { getTransactionExplorerUrl, waitForTransaction } from '../../../StakingConsole/transactionFeedback'

export default function UnstakeModal(props) {
    const [errorMsg, setErrorMsg] = useState('')
    const [stakeInfo, setStakeInfo] = useState({})
    const [unstakeAmountInput, setUnstakeAmountInput] = useState('')
    const [unstakeLoading, setUnstakeLoading] = useState(false)
    const [forceWithdraw, setForceWithdraw] = useState(false)
    const [forceWithdrawLoading, setForceWithdrawLoading] = useState(false)
    const [isMax, setIsMax] = useState(false)
    const [txResult, setTxResult] = useState(null)
    const { t } = useTranslation()
    const initProvider = () => getSelectedWalletEthersProvider()
    const handleErr = (error) => setErrorMsg(mapStakingError(error, t))

    useImperativeHandle(props.onRef, () => ({ setStakeInfo: (info) => new Promise((resolve) => {
        setStakeInfo(info)
        setUnstakeAmountInput('')
        setForceWithdraw(false)
        setIsMax(false)
        setTxResult(null)
        setErrorMsg('')
        resolve()
    }) }))

    const handleForceWithdrawLogic = async () => {
        if (forceWithdrawLoading) return
        setErrorMsg('')
        setForceWithdrawLoading(true)
        const plan = buildEmergencyWithdrawPlan()
        try {
            const contract = new ethers.Contract(stakeInfo.stakeContract, MiningPoolAbi, initProvider().getSigner())
            const tx = await contract[plan.method](...plan.args)
            const result = await waitForTransaction(tx)
            setTxResult({ ...result, mode: 'emergency', amount: maxAmount })
            eventBus.emit('forceWithdrawSuccess')
        } catch (error) { handleErr(error) }
        setForceWithdrawLoading(false)
    }

    const handleUnstakeLogic = async () => {
        if (unstakeLoading) return
        setErrorMsg('')
        const staked = stakeInfo?.stakedAmount / Math.pow(10, stakeInfo?.stakeTokenInfo?.decimals)
        if (isNaN(parseFloat(unstakeAmountInput)) || parseFloat(unstakeAmountInput || 0) <= 0 || parseFloat(unstakeAmountInput) > staked) {
            setErrorMsg(t('请输入正确的数量'))
            return
        }
        setUnstakeLoading(true)
        const plan = buildTokenWithdrawPlan({ input: `${unstakeAmountInput}`, decimals: stakeInfo.stakeTokenInfo.decimals, isMax, stakedAmountBigNumber: stakeInfo.stakedAmountBigNumber })
        try {
            const contract = new ethers.Contract(stakeInfo.stakeContract, MiningPoolAbi, initProvider().getSigner())
            const tx = await contract[plan.method](...plan.args)
            const result = await waitForTransaction(tx)
            setTxResult({ ...result, mode: 'unstake', amount: `${unstakeAmountInput}` })
            eventBus.emit('withdrawSuccess')
        } catch (error) { handleErr(error) }
        setUnstakeLoading(false)
    }

    const maxAmount = stakeInfo?.stakedAmount / Math.pow(10, stakeInfo?.stakeTokenInfo?.decimals)
    const explorerUrl = getTransactionExplorerUrl(stakeInfo.chainId, txResult?.txHash)
    const footer = txResult
        ? <>{explorerUrl && <a className="console-button" href={explorerUrl} target="_blank" rel="noreferrer">{t('查看交易')} <i className="fa fa-external-link-alt" /></a>}<button type="button" className="console-button console-button--primary" onClick={props.handleClose}>{t('完成')}</button></>
        : forceWithdraw
        ? <><button type="button" className="console-button" onClick={() => { setErrorMsg(''); setForceWithdraw(false) }}>{t('取消')}</button><button type="button" className="console-button console-button--danger" disabled={forceWithdrawLoading} onClick={handleForceWithdrawLogic}>{forceWithdrawLoading ? t('处理中...') : t('确认')}</button></>
        : <button type="button" className="console-button console-button--primary" disabled={unstakeLoading} onClick={handleUnstakeLogic}>{unstakeLoading ? t('解除中') : t('解除质押')}</button>
    const successTitle = txResult?.mode === 'emergency' ? t('紧急撤离成功') : t('解除质押成功')
    return <ConsoleDialog show={props.show} onClose={props.handleClose} closeLabel={t('关闭')} title={txResult ? successTitle : forceWithdraw ? t('紧急撤离') : t('解除质押')} description={txResult ? t('交易已确认') : forceWithdraw ? t('紧急撤离将放弃未领取奖励。') : t('输入要解除的质押数量')} icon={txResult ? 'fa-check' : forceWithdraw ? 'fa-exclamation-triangle' : 'fa-unlock'} tone={txResult ? 'success' : forceWithdraw ? 'danger' : 'default'} className={txResult ? 'console-dialog--transaction' : ''} footer={footer}>
        {errorMsg && <p className="console-form-error" role="alert">{errorMsg}</p>}
        {txResult ? <>
            <div className="console-transaction-rail" aria-label={t('交易进度')}>
                <span><i className="fa fa-check" />{t('链上确认')}</span>
                <i />
                <span><i className="fa fa-check" />{t('完成')}</span>
            </div>
            <TransactionSuccessContent
            title={successTitle}
            description={txResult.mode === 'emergency' ? t('质押代币已紧急返回钱包，未领取奖励已放弃。') : t('质押代币已成功返回钱包。')}
            amount={txResult.amount}
            symbol={stakeInfo?.stakeTokenInfo?.symbol}
            amountLabel={t('返回数量')}
            wallet={stakeInfo.currentWallet}
            txHash={txResult.txHash}
            t={t}
        /></> : forceWithdraw ? <p className="console-danger-note">{t('使用紧急撤离后,可立即从矿池中撤出您的所有质押代币, 不会获得任何奖励, 是否使用紧急撤离?')}</p> : <>
            <label className="console-form-label" htmlFor="unstake-amount">{t('解除数量')}</label>
            <div className="console-modal-input"><input id="unstake-amount" value={unstakeAmountInput} inputMode="decimal" onChange={(event) => { setUnstakeAmountInput(event.target.value); setIsMax(false) }} placeholder="0.00" /><button type="button" className="console-max-button" aria-label="MAX" onClick={() => { setUnstakeAmountInput(`${maxAmount}`); setIsMax(true) }}><i className="fa fa-crosshairs" aria-hidden="true" /> MAX</button></div>
            <div className="console-form-summary"><span>{t('已质押')}</span><strong>{maxAmount || 0} {stakeInfo?.stakeTokenInfo?.symbol}</strong></div>
            {stakeInfo.userCanWithdraw && <button type="button" className="console-emergency-action" aria-label={t('紧急撤离')} onClick={() => setForceWithdraw(true)}>
                <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                <span><strong>{t('紧急撤离')}</strong><small>{t('立即取回全部质押本金，但会放弃所有未领取奖励。')}</small></span>
                <i className="fa fa-chevron-right" aria-hidden="true" />
            </button>}
        </>}
    </ConsoleDialog>
}
